/**
 * Peer stack (USE_MEDIA_COORDINATOR): PeerCoordinator, input managers, macro-recorder services, the local
 * editor session and the admin peer handler. Extracted from initializeCoordinators; behaviour unchanged.
 */
import { authState, pauseInputState, machineIdState, shadowcamBindState } from '../api/stores/shared'; // TODO-STRANGLER-FIG: Temporary legacy adapter
import { RecorderCameraPreparation } from './peer/RecorderCameraPreparation';
import { serviceContainer } from '../services/ServiceContainer';
import { getStateStore } from '../api/stores';
import { getElectronStores } from '../api/stores/electronStores';
import { generators, LOCAL_EDITOR_COMPATIBILITY_VERSION } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { InputManager } from './peer/managers/InputManager';
import { CampaignInputManager } from './peer/managers/CampaignInputManager';
import { GamepadManager } from './peer/managers/GamepadManager';
import { ActivityTracker } from './peer/managers/ActivityTracker';
import { AdminPeerHandler } from './peer/handlers/AdminPeerHandler';
import { RecordingControlManager } from '../services/playjector/managers/RecordingControlManager';
import { BroadcastStatusIpcSyncManager } from './session/managers/BroadcastStatusIpcSyncManager';
import { ConnectedUsersIpcSyncManager } from './session/managers/ConnectedUsersIpcSyncManager';
import { createInputConfigStores } from './peer/stores/inputConfigStores';
import { LocalHostApiRendererService } from '../services/local-host-api/LocalHostApiRendererService';
import { LocalHostSessionCoordinator, type LocalHostPackageRuns } from './local-host-session/LocalHostSessionCoordinator';
import { MacroRecorderCaptureSourceManager } from '../services/playjector/managers/MacroRecorderCaptureSourceManager';
import type { MacroVisualTestManager } from '../services/playjector/managers/MacroVisualTestManager';
import { ReferenceCaptureManager } from '../services/playjector/managers/ReferenceCaptureManager';
import { LocalVisualTestAnalysisRunner } from '../services/playjector/managers/LocalVisualTestAnalysisRunner';
import { LocalSourceGraphActionRouter } from './local-host-session/LocalSourceGraphActionRouter';
import { getIpcFunctions } from '../api/modules/ipcInterface';
import type { AppCoordinator } from './app/AppCoordinator';
import type { PavilionCoordinator } from './pavilion/PavilionCoordinator';
import type { SessionCoordinator } from './session/SessionCoordinator';
import type { MediaCoordinator } from './media/MediaCoordinator';
import type { PeerCoordinator } from './peer/PeerCoordinator';
import type { RealtimeSignalingService } from '../services/peer/RealtimeSignalingService';
import { MaintenanceActivityService } from '../services/maintenance/MaintenanceActivityService';
import { PeerMaintenanceAdmissionManager } from './peer/managers/PeerMaintenanceAdmissionManager';
import { ipcReceive } from '../services/IpcMessaging';
import { USE_MAINTENANCE_ACTIVITY_ADMISSION } from '../config/featureFlags';

export interface PeerStackDeps {
  appCoordinator: AppCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionCoordinator: SessionCoordinator;
  mediaCoordinator: MediaCoordinator;
  peerCoordinator: PeerCoordinator;
  realtimeSignalingService: RealtimeSignalingService;
  visualTestManager?: MacroVisualTestManager;
  packageRuns?: LocalHostPackageRuns;
}
export interface PeerStack {
  adminPeerHandler: AdminPeerHandler;
  localHostSessionCoordinator: LocalHostSessionCoordinator;
  connectedUsersIpcSyncManager: ConnectedUsersIpcSyncManager;
}

export async function initializePeerStack(deps: PeerStackDeps): Promise<PeerStack> {
  const { appCoordinator, pavilionCoordinator, sessionCoordinator, mediaCoordinator, peerCoordinator,
    realtimeSignalingService, visualTestManager } = deps;
  let localHostSessionCoordinator: LocalHostSessionCoordinator | null = null;
  const legacyIceState = getStateStore('ipc').iceServerState.currentState;
  const forceTurn = !legacyIceState.allowPeerConnections;

  const ipcService = serviceContainer.ipcService;
  const activity = USE_MAINTENANCE_ACTIVITY_ADMISSION
    ? new MaintenanceActivityService({ ipcService, receive: ipcReceive }) : undefined;
  const maintenanceAdmission = activity ? new PeerMaintenanceAdmissionManager(activity, {
    onUncertain: async peerId => { await peerCoordinator.disconnectLocalPeer(peerId); },
  }) : undefined;

  /** Admin offer is handled before AdminPeerHandler runs; ShadowCam must be bound first or the track can stick on solid green. */
  const PLAYJECTOR_SETSOURCE_STAGGER_MS = 500;
  const SHADOWCAM_SOURCE_ACK_TIMEOUT_MS = 3_000;

  /**
   * Wait for the camera to be enumerable AND for the setSource bind to complete.
   * The camera appears in Chromium's device list before Playjector's 500ms setSource
   * stagger fires, so capturing immediately after enumeration yields the green placeholder.
   * We poll for enumeration, then apply the historical stagger delay to ensure bind.
   */
  const waitForShadowcamEnumerated = async (shadowcamName: string): Promise<void> => {
    const deadline = Date.now() + SHADOWCAM_SOURCE_ACK_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await mediaCoordinator.getDeviceConfig().enumerateDevices();
      const found = mediaCoordinator
        .getDeviceConfig()
        .getShadowcams()
        .some(
          (cam) =>
            cam.displayName?.includes(shadowcamName) || cam.deviceId?.includes(shadowcamName),
        );
      if (found) {
        // Camera is enumerable but setSource (bind) fires 500ms after getSources reply.
        // Wait for that stagger plus a safety margin to avoid capturing the green placeholder.
        await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
        return;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
    }
    console.warn(
      `[Coordinators] Shadowcam ${shadowcamName} not enumerated within ${SHADOWCAM_SOURCE_ACK_TIMEOUT_MS}ms — falling back to setSource stagger`,
    );
    await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
  };

  const beforeAdminMediaCapture = async (): Promise<void> => {
    ipcService.send(generators.camera.getSources());
    await waitForShadowcamEnumerated('ShadowCam03');
  };

  const activateShadowcam = async (shadowcamName: string): Promise<void> => {
    console.log(`[Coordinators] Activating shadowcam ${shadowcamName} via Playjector`);
    // Main mirrors this toggle into the sourced camera set as it passes through, so the
    // getSources reply below binds a display to this camera rather than skipping it.
    ipcService.send(generators.camera.startStopToggle(shadowcamName, true));
    ipcService.send(generators.camera.getSources());
    await waitForShadowcamEnumerated(shadowcamName);
  };

  const deactivateShadowcam = async (shadowcamName: string): Promise<void> => {
    console.log(`[Coordinators] Deactivating on-demand shadowcam ${shadowcamName}`);
    ipcService.send(generators.camera.startStopToggle(shadowcamName, false));
  };

  const recorderCamera = new RecorderCameraPreparation({
    activate: camera => ipcService.send(generators.camera.startStopToggle(camera, true)),
    binding: camera => shadowcamBindState.currentState.bindings[camera],
    refreshSources: () => ipcService.send(generators.camera.getSources()),
    subscribe: listener => {
      const id = shadowcamBindState.addListener(listener);
      return () => shadowcamBindState.removeListener(id);
    },
    now: () => Date.now(),
  });
  await peerCoordinator.init(sessionCoordinator, mediaCoordinator, realtimeSignalingService, {
    maintenanceAdmission,
    iceTransportPolicy: forceTurn ? 'relay' : 'all',
    beforeAdminMediaCapture,
    activateShadowcam,
    deactivateShadowcam,
    prepareRecorderCamera: camera => recorderCamera.prepare(camera),
  });
  const inputStores = createInputConfigStores();

  // Bridge legacy stores -> coordinator-owned stores (strangler fig)
  const legacyStore = getStateStore('ipc');
  legacyStore.gamepadState.addListener((gs) => {
    inputStores.gamepadConfig.updateState({
      autoAssign: gs.autoAssign,
      maxAssignments: gs.maxAssignments,
      shareFlags: [gs.shareGamepadOne, gs.shareGamepadTwo, gs.shareGamepadThree, gs.shareGamepadFour],
    });
  });
  legacyStore.hostRuleState.addListener((rs) => {
    inputStores.inputRules.updateState({ enableGamepadFiltering: rs.enableGamepadFiltering });
  });
  pauseInputState.addListener((ps) => {
    inputStores.pauseInput.updateState(ps);
  });

  const gamepadManager = new GamepadManager({
    ipcService,
    gamepadConfigStore: inputStores.gamepadConfig,
    inputRulesStore: inputStores.inputRules,
    appCoordinator,
  });

  const activityTracker = new ActivityTracker({
    afkTimeoutMs: 10_000,
    onAfkChanged: (peerId, afk) => {
      peerCoordinator.events.afkStateChanged.emit({ peerId, afk });
    },
    onKick: (peerId) => {
      peerCoordinator.kickPeer(peerId, 'kicked:afk');
    },
  });

  const recordingControlManager = new RecordingControlManager({
    playjectorService: serviceContainer.playjectorService,
    sendIpc: (msg: unknown) => ipcService.send(msg as any),
    getHostAuthId: () => appCoordinator.getHostAuthId(),
  });
  serviceContainer.setRecordingControlManager(recordingControlManager);

  const captureSourceManager = new MacroRecorderCaptureSourceManager({
    functions: {
      ...getIpcFunctions(),
      processEnumRunning: () => ipcService.send(generators.process.processEnumRunning(
        appCoordinator.getHostAuthId())),
      processSelectWindow: (processId, windowHandle) => ipcService.send(generators.process.processSelect(
        appCoordinator.getHostAuthId(), processId, undefined, 'window', windowHandle)),
    },
    setPreviewCameraSurfing: (enabled) =>
      ipcService.send(generators.camera.autoPick('ShadowCam03', enabled)),
    onSourceSelected: (source) => peerCoordinator.refreshMacroRecorderVideoCapture(source),
    refreshCameras: () => mediaCoordinator.refreshDeviceList(),
    getSnapshot: () => {
      const legacy = getStateStore('ipc');
      const video = legacy.videoState.currentState;
      const display = legacy.displayState.currentState;
      return {
        selectedDevice: video.selectedDevice,
        selectedDisplayId: display.info.selectedDisplayId,
        displays: display.displays.map(({ displayId, properties }) => {
          const bounds = properties.bounds;
          const desktop = properties.desktopCoordinates;
          const x = Number(bounds.x ?? bounds.left ?? desktop.left ?? 0);
          const y = Number(bounds.y ?? bounds.top ?? desktop.top ?? 0);
          const width = Number(bounds.width ?? ((bounds.right ?? desktop.right ?? 0) - x));
          const height = Number(bounds.height ?? ((bounds.bottom ?? desktop.bottom ?? 0) - y));
          return {
            displayId,
            displayName: properties.displayName,
            width,
            height,
            x,
            y,
            isPrimary: properties.isPrimary,
          };
        }),
        windowCapturePid: video.windowCapturePid,
        runningApps: legacy.runningApplicationState.currentState.map(([name, app]) => ({
          name,
          processId: app.processId,
          windowHandle: app.windowHandle,
        })),
        cameras: mediaCoordinator.getDeviceConfig().getCameras(),
      };
    },
  });

  const referenceCaptureClient = serviceContainer.playjectorService.referenceCapture;
  const referenceCaptureManager = referenceCaptureClient
    ? new ReferenceCaptureManager(referenceCaptureClient, () => visualTestManager?.isRunning() ?? false,
      (sessionId, result) => { localHostSessionCoordinator?.pushReferenceCaptureSample(sessionId, result); })
    : undefined;
  localHostSessionCoordinator = new LocalHostSessionCoordinator(
    new LocalHostApiRendererService(),
    peerCoordinator,
    ipcService,
    serviceContainer.playjectorService,
    recordingControlManager,
    {
      machineId: () => machineIdState.currentState,
      branch: constants.BRANCH,
      version: constants.VERSION,
      editorCompatibilityVersion: LOCAL_EDITOR_COMPATIBILITY_VERSION,
      realtimeConnected: () => serviceContainer.realtimeService.isConnected(),
    },
    captureSourceManager,
    visualTestManager,
    referenceCaptureManager,
    { ...deps.packageRuns, ...(serviceContainer.playjectorService.macroAnalysis && deps.packageRuns?.bridge ? {
      sourceGraphs: new LocalSourceGraphActionRouter(new LocalVisualTestAnalysisRunner(serviceContainer.playjectorService.macroAnalysis), deps.packageRuns.bridge),
    } : {}) },
  );
  localHostSessionCoordinator.init();

  const adminPeerHandler = new AdminPeerHandler({
    ipcService,
    realtimeService: serviceContainer.realtimeService,
    appCoordinator,
    peerCoordinator,
    pavilionCoordinator,
    sessionPresenceUpdater: sessionCoordinator,
    recordingControlManager,
    captureSourceManager,
    localHostSession: localHostSessionCoordinator,
  });

  const inputManager = new InputManager({
    peerCoordinator,
    ipcService,
    pauseInputStore: inputStores.pauseInput,
    gamepadManager,
    adminPeerHandler,
    activityTracker,
    localHostSession: localHostSessionCoordinator,
  });

  peerCoordinator.registerInputStores(inputStores);
  peerCoordinator.registerGamepadManager(gamepadManager);
  peerCoordinator.registerActivityTracker(activityTracker);
  peerCoordinator.getTypeRegistry().register('admin', adminPeerHandler);

  peerCoordinator.events.dataReceived.addListener(({ peerId, data }) => {
    inputManager.handleMessage(peerId, data);
  });

  peerCoordinator.events.onPlayerLeft.addListener(({ peerId }) => {
    gamepadManager.removePeer(peerId);
    activityTracker.removePeer(peerId);
  });
  peerCoordinator.events.onAdminLeft.addListener(({ peerId }) => {
    localHostSessionCoordinator?.releaseRecorderInput(peerId);
    gamepadManager.removePeer(peerId);
  });

  // Legacy playjectorHostModeEvents.ts was the only sender of hub.broadcastStatus,
  // but it's gated off when USE_MESSAGE_ROUTER is true. Playjector relies on
  // that message to run its FlushKeyboard/FlushMouse/FlushTouches/FlushGamepads
  // path on broadcast-state transitions; without this bridge, stuck inputs
  // persist across stream stops in the coordinator flow.
  new BroadcastStatusIpcSyncManager({
    ipcService,
    sessionCoordinator,
    getHostAuthId: () => appCoordinator.getHostAuthId(),
  }).start();

  // Bridge live connected user/admin peer count -> PlayJector so the service can gate
  // CPU/GPU affinity on real activity. Deliberately NOT coupled to broadcast status.
  const connectedUsersIpcSyncManager = new ConnectedUsersIpcSyncManager({
    ipcService,
    peerCoordinator,
    getHostAuthId: () => appCoordinator.getHostAuthId(),
  });
  connectedUsersIpcSyncManager.start();

  // Bridge machineIdState -> truncated machine ID replication to players (strangler fig)
  const electronStores = getElectronStores();
  let currentTruncMachineId = '';

  const broadcastTruncMachineId = () => {
    if (!currentTruncMachineId) return;
    console.log('[MachineId-Bridge] Broadcasting truncMachineId to all peers');
    const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
    peerCoordinator.broadcastToAll(JSON.stringify(msg));
  };

  electronStores.machineIdState.addListener((machineId) => {
    currentTruncMachineId = machineId.substring(6, 20);
    broadcastTruncMachineId();
  });
  if (electronStores.machineIdState.currentState) {
    currentTruncMachineId = electronStores.machineIdState.currentState.substring(6, 20);
  }

  peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => {
    console.log(`[MachineId-Bridge] onPlayerJoined: ${peerId}, truncMachineId=${currentTruncMachineId || '(empty)'}`);
    if (!currentTruncMachineId) return;
    const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
    const sent = peerCoordinator.sendToPeer(peerId, JSON.stringify(msg));
    console.log(`[MachineId-Bridge] sendToPeer result: ${sent}`);
  });

  // Forward player-joined events to PavilionCoordinator so it can apply
  // campaign-configured matchmaking cooldowns (e.g. Hitman 30-min contract clock).
  // The coordinator is idempotent within a launch instance, so multiple joins
  // for the same launch only start the timer once.
  peerCoordinator.events.onPlayerJoined.addListener(() => {
    pavilionCoordinator.notifyPlayerJoined();
  });

  // Gate player input to the active campaign's supported input methods
  // (HostCampaignConfig.supportedInputs). Clamps PeerPermissions on join and
  // whenever the campaign config loads/clears (GitHub #13807), and notifies
  // each player client of its granted access via setInputAccess.
  const campaignInputManager = new CampaignInputManager({
    peerCoordinator,
    pavilionCoordinator,
    getHostAuthId: () => authState.currentState.hostAuthId,
  });
  campaignInputManager.init();

  return { adminPeerHandler, localHostSessionCoordinator: localHostSessionCoordinator as LocalHostSessionCoordinator, connectedUsersIpcSyncManager };
}
